import { useMemo } from "react";
import type { Data } from "plotly.js-dist-min";
import { useLocale } from "@hooks/useLocale";
import { ChartFrame, useChartTheme } from "@components/chart";
import type { PadRollupOut, PlanTimelineResponse, PlanTimelineBar } from "@api/types";
import { useGetPadTimelineQuery } from "@api/pApi";

const DAY_MS = 86_400_000;

/**
 * Pad time (§12) — the pad Gantt (wells as rows, the rig moving well-to-well,
 * rig-move gaps, per-well spud→TD) plus the pad time rollup (days, NPT, CPE/rig
 * utilization). Utilization is served by Oxbo-P (online / elapsed, §14).
 */
export function PadTimeSection({
  siteId,
  rollup,
}: {
  siteId: string;
  rollup: PadRollupOut;
}) {
  const { t } = useLocale();
  const { data: timeline } = useGetPadTimelineQuery(siteId, { skip: !siteId });

  return (
    <>
      <h2 className="pad-section-head">{t("pad.time")}</h2>

      <div style={{ marginTop: 12 }}>
        {timeline && timeline.tracks.length ? (
          <PadGantt timeline={timeline} />
        ) : (
          <div className="card">
            <div className="empty-state">{t("pad.noTimeline")}</div>
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("pad.timeRollup")}</h3>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("pad.col.well")}</th>
                <th className="num">{t("pad.col.daysOnWell")}</th>
                <th>{t("pad.col.sequence")}</th>
              </tr>
            </thead>
            <tbody>
              {rollup.per_well.map((w, i) => (
                <tr key={w.well_uwi} data-owner="p">
                  <td>{w.common_name}</td>
                  <td className="num tab-nums">{w.days}</td>
                  <td className="tab-nums">{i + 1}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th>{t("pad.col.padTotal")}</th>
                <td className="num tab-nums">{rollup.pad_days}</td>
                <td>—</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="pad-note">
          {t("pad.rigUtilNote", {
            pct: rollup.rig_utilization_pct != null ? `${rollup.rig_utilization_pct}%` : "—",
          })}
        </p>
      </div>
    </>
  );
}

/**
 * A Plotly horizontal-bar Gantt of the pad timeline. Each track (rig / frac /
 * CT / well) is a row band; each bar is drawn as a base+length segment on a
 * date axis. Actual bars are solid; planned-only bars are lighter; NPT bars use
 * the crit hue; offline bars are hatched-adjacent (rendered dimmed).
 */
function PadGantt({ timeline }: { timeline: PlanTimelineResponse }) {
  const { t } = useLocale();
  const theme = useChartTheme();

  const { data, rangeStart, rangeEnd, tickvals, ticktext } = useMemo(() => {
    // Flatten to (row-label, bar) pairs; each track is one y-row.
    const rows: { label: string; bar: PlanTimelineBar }[] = [];
    for (const track of timeline.tracks) {
      for (const bar of track.bars) rows.push({ label: track.label, bar });
    }

    const toMs = (s: string | null | undefined): number | null =>
      s ? new Date(s).getTime() : null;
    const spanStart = (b: PlanTimelineBar) => toMs(b.actual_start) ?? toMs(b.planned_start);
    const spanEnd = (b: PlanTimelineBar) => toMs(b.actual_end) ?? toMs(b.planned_end);

    const allStarts = rows.map((r) => spanStart(r.bar)).filter((n): n is number => n != null);
    const allEnds = rows.map((r) => spanEnd(r.bar)).filter((n): n is number => n != null);
    const min = allStarts.length ? Math.min(...allStarts) : Date.now();
    const max = allEnds.length ? Math.max(...allEnds) : min + 30 * DAY_MS;

    const barColor = (b: PlanTimelineBar): string => {
      if (b.is_npt) return theme.crit;
      if (b.is_offline) return theme.oxboU;
      const isActual = b.actual_start != null;
      return isActual ? theme.oxboZ : theme.oxboM;
    };

    // One horizontal-bar trace per bar (base = start, x = duration in days).
    const traces: Data[] = rows.map(({ label, bar }) => {
      const start = spanStart(bar) ?? min;
      const end = spanEnd(bar) ?? start + DAY_MS;
      const durationDays = Math.max(0.5, (end - start) / DAY_MS);
      const baseDays = (start - min) / DAY_MS;
      const planned = bar.actual_start == null;
      return {
        type: "bar",
        orientation: "h",
        y: [label],
        x: [durationDays],
        base: [baseDays],
        width: 0.55,
        marker: {
          color: barColor(bar),
          opacity: planned ? 0.45 : 0.95,
          line: { width: 0 },
        },
        name: bar.label,
        hovertemplate: `${bar.label}<br>${bar.op_type}<extra></extra>`,
        showlegend: false,
      } as Data;
    });

    // Day-offset ticks → date labels every ~7 days.
    const totalDays = Math.ceil((max - min) / DAY_MS);
    const step = totalDays > 28 ? 7 : totalDays > 10 ? 3 : 1;
    const tv: number[] = [];
    const tt: string[] = [];
    for (let d = 0; d <= totalDays; d += step) {
      tv.push(d);
      tt.push(new Date(min + d * DAY_MS).toLocaleDateString(undefined, { month: "short", day: "numeric" }));
    }

    return {
      data: traces,
      rangeStart: 0,
      rangeEnd: totalDays,
      tickvals: tv,
      ticktext: tt,
    };
  }, [timeline, theme]);

  const rowCount = useMemo(
    () => new Set(timeline.tracks.map((tr) => tr.label)).size,
    [timeline],
  );

  return (
    <ChartFrame
      title={t("pad.chart.gantt")}
      ariaLabel={t("pad.chart.gantt")}
      height={Math.max(200, rowCount * 46 + 80)}
      data={data}
      xTitle={t("pad.chart.date")}
      layout={{
        barmode: "overlay",
        bargap: 0.35,
        xaxis: {
          range: [rangeStart, rangeEnd],
          tickvals,
          ticktext,
        },
        yaxis: { automargin: true, autorange: "reversed" },
        margin: { l: 140, r: 16, t: 10, b: 44 },
      }}
    />
  );
}
